// Super Admin "View as User" helpers.
// The real authentication identity remains Super Admin. This state controls
// the visible role and the data scope shown by the selected-user view.

export const SUPERADMIN_VIEW_KEY = "superadmin_view_target";

const readJson = (key) => {
  try {
    const raw = sessionStorage.getItem(key) || localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const getStoredSuperAdminTarget = () =>
  readJson(SUPERADMIN_VIEW_KEY) || readJson("superadmin_dashboard_target");

export const isSuperAdminViewing = () => {
  const target = getStoredSuperAdminTarget();
  return !!target?.role;
};

export const enterSuperAdminView = (target) => {
  const value = JSON.stringify({ ...target, selected_at: target?.selected_at || Date.now() });
  sessionStorage.setItem(SUPERADMIN_VIEW_KEY, value);
  localStorage.setItem(SUPERADMIN_VIEW_KEY, value);
  sessionStorage.setItem("superadmin_dashboard_target", value);
  localStorage.setItem("superadmin_dashboard_target", value);
};

export const exitSuperAdminView = () => {
  [SUPERADMIN_VIEW_KEY, "superadmin_dashboard_target"].forEach((key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
};

export const getEffectiveRole = (actualRole) => {
  const normalized = String(actualRole || "").toLowerCase();
  if (normalized !== "superadmin") return normalized;
  return getStoredSuperAdminTarget()?.role?.toLowerCase() || normalized;
};

export const getEffectiveDisplayUser = (actualUser) => {
  const actualRole = String(actualUser?.role || "").toLowerCase();
  if (actualRole !== "superadmin") return actualUser;
  const target = getStoredSuperAdminTarget();
  if (!target?.role) return actualUser;
  return {
    ...actualUser,
    ...target,
    role: target.role,
    isSuperAdminView: true,
    actualRole: "superadmin",
  };
};

const valueOf = (record, field) => {
  if (!record || !field) return null;
  const value = record[field];
  if (value !== null && value !== undefined && String(value).trim() !== "") return value;
  return null;
};

const valuesEqual = (a, b) =>
  a !== null && a !== undefined && b !== null && b !== undefined &&
  String(a).trim().toLowerCase() === String(b).trim().toLowerCase();

const anyFieldEquals = (record, fields, expected) =>
  fields.some((field) => valuesEqual(valueOf(record, field), expected));

const scopeMatches = (record, target) => {
  const targetState = target?.state || target?.state_name;
  const targetRegion = target?.region || target?.region_name;
  const recordState = record?.state || record?.state_name || record?.stateName;
  const recordRegion = record?.region || record?.region_name || record?.regionName;

  const hasState = targetState && recordState;
  const hasRegion = targetRegion && recordRegion;

  if (hasState && !valuesEqual(targetState, recordState)) return false;
  if (hasRegion && !valuesEqual(targetRegion, recordRegion)) return false;
  return !!(hasState || hasRegion);
};

// Filter a patient collection according to the selected Super Admin target.
// If no target is active, the caller's normal role rules remain unchanged.
export const filterPatientsForSuperAdminView = (patients, target = getStoredSuperAdminTarget()) => {
  if (!Array.isArray(patients)) return [];
  if (!target?.role) return patients;

  const role = String(target.role).trim().toLowerCase();
  // Organization-wide patient visibility for selected full-access roles.
  if (["admin", "superadmin", "ncm", "pdcare", "hod"].includes(role)) return patients;
  const id = target.id;

  const mapping = {
    patient: ["patient_id", "id"],
    dealer: ["dealer_id"],
    doctor: ["doctor_id", "current_doctor_id", "implant_doctor_id"],
    cm: ["cm_id"],
    rcm: ["rcm_id"],
    rsm: ["rsm_id"],
    hod: ["hod_id", "hod_user_id", "hodId", "user_id"],
    ncm: ["ncm_id", "ncm_user_id", "ncmId", "user_id"],
    pdcare: ["pdcare_id", "pdcare_user_id", "pdcareId", "user_id"],
    admin: ["admin_id", "admin_user_id", "user_id"],
  };

  const fields = mapping[role] || ["user_id", "owner_id", "created_by", "assigned_user_id"];

  const direct = patients.filter((patient) => anyFieldEquals(patient, fields, id));
  if (direct.length > 0) return direct;

  // Match by the explicit mapped IDs carried in the selected target.
  const idFields = ["patient_id", "dealer_id", "doctor_id", "cm_id", "rcm_id", "rsm_id"];
  const targetIdValues = idFields.map((field) => target?.[field]).filter((v) => v !== null && v !== undefined && v !== "");
  if (targetIdValues.length) {
    const byTargetIds = patients.filter((patient) =>
      idFields.some((field) => targetIdValues.some((expected) => valuesEqual(valueOf(patient, field), expected)))
    );
    if (byTargetIds.length > 0) return byTargetIds;
  }

  // HOD/NCM/PDCare records may be scoped by state/region rather than a direct
  // patient foreign key. Use only explicit scope values from the selected user.
  const byScope = patients.filter((patient) => scopeMatches(patient, target));
  if (byScope.length > 0) return byScope;

  // Never fall back to all records while Super Admin is viewing a user.
  return [];
};

// Filter non-patient records (infections, DAR, activity, reports, etc.) using
// explicit role mappings. The function intentionally returns [] when a
// Super-Admin target is active but no mapping exists, preventing accidental
// exposure of organization-wide records.
export const filterRecordsForSuperAdminView = (
  records,
  target = getStoredSuperAdminTarget(),
  options = {}
) => {
  if (!Array.isArray(records)) return [];
  if (!target?.role) return records;

  const role = String(target.role).trim().toLowerCase();
  // Reports follow the same full-access scope as patient records.
  if (["admin", "superadmin", "ncm", "pdcare", "hod"].includes(role)) return records;
  const directFieldsByRole = {
    patient: ["patient_id", "patientId"],
    dealer: ["dealer_id", "dealerId"],
    doctor: ["doctor_id", "doctorId", "current_doctor_id", "implant_doctor_id"],
    cm: ["cm_id", "clinicalManager", "clinical_manager_id", "user_id", "assigned_user_id"],
    rcm: ["rcm_id", "rcm", "user_id", "assigned_user_id"],
    rsm: ["rsm_id", "rsm", "user_id", "assigned_user_id"],
    hod: ["hod_id", "hod_user_id", "user_id", "assigned_user_id"],
    ncm: ["ncm_id", "ncm_user_id", "user_id", "assigned_user_id"],
    pdcare: ["pdcare_id", "pdcare_user_id", "user_id", "assigned_user_id"],
    admin: ["admin_id", "admin_user_id", "user_id", "created_by"],
    campaignadmin: ["campaign_admin_id", "created_by", "user_id", "owner_id"],
    campaignreport: ["campaign_report_id", "created_by", "user_id", "owner_id"],
  };

  const directFields = [
    ...(options.directFields || []),
    ...(directFieldsByRole[role] || []),
  ];

  const targetValues = [
    target.id,
    target.patient_id,
    target.dealer_id,
    target.doctor_id,
    target.cm_id,
    target.rcm_id,
    target.rsm_id,
    target.user_id,
  ].filter((v) => v !== null && v !== undefined && v !== "");

  const direct = records.filter((record) =>
    directFields.some((field) => targetValues.some((expected) => valuesEqual(valueOf(record, field), expected)))
  );
  if (direct.length > 0) return direct;

  // Many report APIs return the related patient as `patient: {...}` instead
  // of copying cm_id/rcm_id/etc. to the report row. Apply the same mapping
  // rules to that nested patient object.
  const nestedPatient = records.filter((record) => {
    if (!record?.patient) return false;
    return filterPatientsForSuperAdminView([record.patient], target).length > 0;
  });
  if (nestedPatient.length > 0) return nestedPatient;

  if (target.email) {
    const byEmail = records.filter((record) =>
      ["email", "user_email", "created_by_email", "owner_email"].some((field) =>
        valuesEqual(valueOf(record, field), target.email)
      )
    );
    if (byEmail.length > 0) return byEmail;
  }

  const byScope = records.filter((record) => scopeMatches(record, target));
  if (byScope.length > 0) return byScope;

  // If caller supplies the mapped patient IDs, use them as a second-level
  // relation for records that only carry patient_id.
  const patientIds = options.patientIds || [];
  if (patientIds.length > 0) {
    const byPatient = records.filter((record) =>
      ["patient_id", "patientId"].some((field) =>
        patientIds.some((pid) => valuesEqual(valueOf(record, field), pid))
      )
    );
    if (byPatient.length > 0) return byPatient;
  }

  return [];
};
